import { useState } from "react";
import { CalendarClock, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
  datetimeLocalToIso,
  toDatetimeLocalValue,
} from "@/lib/datetimeLocal";

export type ActionStatus =
  | "none"
  | "watching"
  | "authorized"
  | "submitted"
  | "removed"
  | "rejected";

export type TakedownChannel =
  | "brand_registry"
  | "seller_central"
  | "public_report"
  | "community_help_email";

export type TakedownOutcome = "removed" | "rejected" | "no_response";

export type TakedownAttempt = {
  id: string;
  channel: TakedownChannel;
  filedBy: string;
  amazonCaseId: string | null;
  submittedAt: string;
  resolvedAt: string | null;
  outcome: TakedownOutcome | null;
  notes: string | null;
};

const CHANNEL_LABELS: Record<TakedownChannel, string> = {
  brand_registry: "Brand Registry",
  seller_central: "Seller Central",
  public_report: "Public Report",
  community_help_email: "community-help email",
};

const STATUS_LABELS: Record<ActionStatus, string> = {
  none: "No action",
  watching: "Watching",
  authorized: "Authorized · awaiting filing",
  submitted: "Submitted",
  removed: "Removed",
  rejected: "Rejected",
};

function statusClass(status: ActionStatus) {
  if (status === "removed")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900";
  if (status === "submitted")
    return "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900";
  if (status === "authorized")
    return "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900";
  if (status === "rejected")
    return "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900";
  if (status === "watching")
    return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900";
  return "bg-muted text-muted-foreground ring-border";
}

function OverallStatusControls({
  reviewId,
  current,
  hasAttempts,
  onChange,
}: {
  reviewId: string;
  current: ActionStatus;
  hasAttempts: boolean;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<ActionStatus | null>(null);
  const terminal = current === "submitted" || current === "removed" || current === "rejected";

  async function setStatus(next: ActionStatus) {
    if (next === current) return;
    setBusy(next);
    const res = await api(`/admin/reviews/${reviewId}/action-status`, {
      method: "PATCH",
      body: JSON.stringify({ actionStatus: next }),
    });
    setBusy(null);
    if (!res.ok) {
      alert(res.error ?? "Status update failed");
      return;
    }
    onChange();
  }

  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span className="text-xs text-muted-foreground">Overall status:</span>
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusClass(
          current
        )}`}
      >
        {STATUS_LABELS[current]}
      </span>
      {!terminal && (
        <Button
          variant={current === "watching" ? "default" : "outline"}
          size="sm"
          disabled={busy !== null}
          onClick={() => setStatus(current === "watching" ? "none" : "watching")}
          className="h-7 text-xs"
        >
          {busy === "watching" && <Loader2 className="h-3 w-3 animate-spin" />}
          Watching
        </Button>
      )}
      {current !== "none" && !hasAttempts && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => setStatus("none")}
          className="text-xs text-muted-foreground underline-offset-4 hover:underline"
        >
          Reset
        </button>
      )}
      {terminal && hasAttempts && (
        <span className="text-xs text-muted-foreground">
          Update the case outcome below to change this status.
        </span>
      )}
      {terminal && !hasAttempts && (
        <span className="text-xs text-amber-700 dark:text-amber-300">
          Legacy status without a logged attempt. Log the attempt or reset it.
        </span>
      )}
    </div>
  );
}

function TakedownAttemptsPanel({
  reviewId,
  attempts,
  onChange,
}: {
  reviewId: string;
  attempts: TakedownAttempt[];
  onChange: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [channel, setChannel] = useState<TakedownChannel>("public_report");
  const [outcome, setOutcome] = useState<"" | TakedownOutcome>("");
  const [amazonCaseId, setAmazonCaseId] = useState("");
  const [submittedAt, setSubmittedAt] = useState("");
  const [submittedAtEdited, setSubmittedAtEdited] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingAttemptId, setEditingAttemptId] = useState<string | null>(null);
  const [editingSubmittedAt, setEditingSubmittedAt] = useState("");
  const [savingAttemptId, setSavingAttemptId] = useState<string | null>(null);

  function startAdding() {
    setSubmittedAt(toDatetimeLocalValue(new Date()));
    setSubmittedAtEdited(false);
    setAdding(true);
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const submittedAtIso = submittedAtEdited
      ? datetimeLocalToIso(submittedAt)
      : new Date().toISOString();
    if (!submittedAtIso) {
      setError("Enter a valid submission date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api(`/admin/reviews/${reviewId}/takedown-attempts`, {
        method: "POST",
        body: JSON.stringify({
          channel,
          amazonCaseId: amazonCaseId.trim() || undefined,
          submittedAt: submittedAtIso,
          outcome: outcome || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      if (!res.ok) {
        setError(res.error ?? "Could not log the attempt.");
        return;
      }
      setAdding(false);
      setOutcome("");
      setAmazonCaseId("");
      setSubmittedAt("");
      setSubmittedAtEdited(false);
      setNotes("");
      onChange();
    } catch {
      setError("Could not reach Reputely. Try logging the attempt again.");
    } finally {
      setSubmitting(false);
    }
  }

  function startEditingAttempt(attempt: TakedownAttempt) {
    setEditingAttemptId(attempt.id);
    setEditingSubmittedAt(
      toDatetimeLocalValue(new Date(attempt.submittedAt))
    );
  }

  async function saveAttemptSubmittedAt(attemptId: string) {
    const submittedAtIso = datetimeLocalToIso(editingSubmittedAt);
    if (!submittedAtIso) {
      alert("Enter a valid submission date and time.");
      return;
    }
    setSavingAttemptId(attemptId);
    try {
      const res = await api(`/admin/takedown-attempts/${attemptId}`, {
        method: "PATCH",
        body: JSON.stringify({ submittedAt: submittedAtIso }),
      });
      if (!res.ok) {
        alert(res.error ?? "Could not update the submission time.");
        return;
      }
      setEditingAttemptId(null);
      onChange();
    } catch {
      alert("Could not reach Reputely. Try saving the submission time again.");
    } finally {
      setSavingAttemptId(null);
    }
  }

  async function setAttemptOutcome(attemptId: string, outcome: TakedownOutcome) {
    const res = await api(`/admin/takedown-attempts/${attemptId}`, {
      method: "PATCH",
      body: JSON.stringify({ outcome }),
    });
    if (!res.ok) {
      alert(res.error ?? "Could not update outcome.");
      return;
    }
    onChange();
  }

  async function deleteAttempt(attemptId: string) {
    if (!window.confirm("Delete this takedown attempt?")) return;
    const res = await api(`/admin/takedown-attempts/${attemptId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      alert(res.error ?? "Could not delete attempt.");
      return;
    }
    onChange();
  }

  return (
    <div className="rounded-md border border-border bg-card text-card-foreground">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-medium text-muted-foreground">
          Takedown attempts ({attempts.length})
        </span>
        {!adding && (
          <Button size="sm" variant="outline" onClick={startAdding}>
            Log attempt
          </Button>
        )}
      </div>

      {adding && (
        <form onSubmit={onSubmit} className="space-y-2 px-3 py-3 text-xs">
          <label className="block">
            <span className="font-medium text-muted-foreground">Channel</span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as TakedownChannel)}
              className="mt-1 h-8 w-full rounded-lg border border-input bg-background px-2.5 text-xs text-foreground dark:bg-input/30 dark:scheme-dark"
            >
              <option value="public_report">Public Report (anyone)</option>
              <option value="community_help_email">community-help email (anyone)</option>
              <option value="brand_registry">Brand Registry (RAV)</option>
              <option value="seller_central">Seller Central (Help / Account Health)</option>
            </select>
          </label>
          <label className="block">
            <span className="font-medium text-muted-foreground">
              Amazon case ID (optional)
            </span>
            <Input
              value={amazonCaseId}
              onChange={(e) => setAmazonCaseId(e.target.value)}
              placeholder="e.g. 12345678901"
              className="mt-1 h-8 text-xs"
            />
          </label>
          <label className="block">
            <span className="font-medium text-muted-foreground">
              Submitted at
            </span>
            <Input
              type="datetime-local"
              value={submittedAt}
              onChange={(e) => {
                setSubmittedAt(e.target.value);
                setSubmittedAtEdited(true);
              }}
              required
              className="mt-1 h-8 text-xs dark:scheme-dark"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Uses {Intl.DateTimeFormat().resolvedOptions().timeZone}. Edit this
              when backfilling an Amazon case; otherwise Reputely records the
              time you click Log attempt.
            </span>
          </label>
          <label className="block">
            <span className="font-medium text-muted-foreground">Current outcome</span>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as "" | TakedownOutcome)}
              className="mt-1 h-8 w-full rounded-lg border border-input bg-background px-2.5 text-xs text-foreground dark:bg-input/30 dark:scheme-dark"
            >
              <option value="">Submitted, awaiting response</option>
              <option value="removed">Removed</option>
              <option value="rejected">Rejected</option>
              <option value="no_response">No response</option>
            </select>
          </label>
          <label className="block">
            <span className="font-medium text-muted-foreground">Notes (optional)</span>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Filed with evidence copied from the review page."
              className="mt-1 min-h-14 text-xs"
            />
          </label>
          <div className="flex items-center gap-2 pt-1">
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
              {submitting ? "Logging..." : "Log attempt"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </form>
      )}

      {attempts.length > 0 && (
        <ul className="divide-y divide-border text-xs">
          {attempts.map((attempt) => (
            <li key={attempt.id} className="px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{CHANNEL_LABELS[attempt.channel]}</span>
                <span className="text-muted-foreground">
                  filed{" "}
                  {new Date(attempt.submittedAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
                {attempt.amazonCaseId && (
                  <span className="font-mono text-[11px] text-muted-foreground">
                    case {attempt.amazonCaseId}
                  </span>
                )}
                {attempt.outcome && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset ${
                      attempt.outcome === "removed"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900"
                        : attempt.outcome === "rejected"
                          ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900"
                          : "bg-muted text-muted-foreground ring-border"
                    }`}
                  >
                    {attempt.outcome.replace("_", " ")}
                  </span>
                )}
                <div className="ml-auto flex max-w-full flex-wrap justify-end gap-1">
                  {!attempt.outcome && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAttemptOutcome(attempt.id, "removed")}
                        className="h-6 text-[11px]"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Removed
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAttemptOutcome(attempt.id, "rejected")}
                        className="h-6 text-[11px]"
                      >
                        Rejected
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAttemptOutcome(attempt.id, "no_response")}
                        className="h-6 text-[11px]"
                      >
                        No response
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startEditingAttempt(attempt)}
                    className="h-6 text-[11px]"
                  >
                    <CalendarClock className="h-3 w-3" />
                    Edit time
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteAttempt(attempt.id)}
                    className="h-6 text-[11px] text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    aria-label="Delete takedown attempt"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                </div>
              </div>
              {attempt.notes && (
                <p className="mt-1 text-[11px] italic text-muted-foreground">
                  {attempt.notes}
                </p>
              )}
              {editingAttemptId === attempt.id && (
                <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-muted/50 p-2">
                  <label className="min-w-56 flex-1">
                    <span className="font-medium text-muted-foreground">
                      Submitted at
                    </span>
                    <Input
                      type="datetime-local"
                      value={editingSubmittedAt}
                      onChange={(e) => setEditingSubmittedAt(e.target.value)}
                      className="mt-1 h-8 text-xs dark:scheme-dark"
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    disabled={savingAttemptId === attempt.id}
                    onClick={() => saveAttemptSubmittedAt(attempt.id)}
                  >
                    {savingAttemptId === attempt.id && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                    Save time
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingAttemptId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {attempts.length === 0 && !adding && (
        <p className="px-3 py-3 text-[11px] text-muted-foreground">
          Log the submission here. This is what moves the review to submitted,
          removed, or rejected.
        </p>
      )}
    </div>
  );
}

export function AdminReviewLifecycleControls({
  reviewId,
  actionStatus,
  attempts,
  onChange,
}: {
  reviewId: string;
  actionStatus: ActionStatus;
  attempts: TakedownAttempt[];
  onChange: () => void;
}) {
  return (
    <>
      <OverallStatusControls
        reviewId={reviewId}
        current={actionStatus}
        hasAttempts={attempts.length > 0}
        onChange={onChange}
      />
      <TakedownAttemptsPanel
        reviewId={reviewId}
        attempts={attempts}
        onChange={onChange}
      />
    </>
  );
}
